/** Every word a field's state and source wear (SA-1196, spec §5.2): the
 * generated form, the Needs panel, the table editor, the intake surfaces and
 * the Compass views all read these and word nothing themselves. */
import { EVENT_LABELS } from '@/components/records/eventLabels'
import { isWindow, windowWords } from '@/components/records/window'
import type { FieldSource } from '@/types/process'
import { SUGGESTED_BY_AI, formatConfidence } from './proposalText'
import { whenText } from './stepLiveText'
import { formatCell } from './tableRows'

export const REQUIRED = 'Required'
export const CHANGED = 'changed'
export const RESTORE = 'Use the pre-filled value'
export const NEW_ROW = 'new'
export const PREFILLED = 'pre-filled'
export const STALE_WORDS = 'from before the revision or re-open'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const VIA_WORDS: Record<string, string> = {
  intake_email: 'the enquiry email', intake_form: 'the intake form',
  intake_conversation: 'the conversation', intake_whatsapp_group: 'the group message',
}
const EXTRACTED_WORDS: Record<string, string> = { email: 'the email', 'whatsapp-group': 'the group message', paste: 'the pasted text' }

/** `28 Sep 2026` from an ISO day or datetime; anything else as given. */
export function dayWords(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  const month = MONTHS[Number(m[2]) - 1]
  return month ? `${Number(m[3])} ${month} ${m[1]}` : iso
}

export function changedFrom(was: string): string {
  return `changed from ${was}`
}

/** The longest worded value a "was" line prints: the record timeline's
 * `PRODUCED_MAX` measure, cut the same way. The line is also the control's
 * accessible description, so a long text never reads out whole. */
export const WAS_MAX = 160

function cut(words: string): string {
  return words.length > WAS_MAX ? `${words.slice(0, WAS_MAX - 1).trimEnd()}…` : words
}

/** `was 582866`; a table counts its rows, a window reads its words; a value past `WAS_MAX` is cut. */
export function wasText(value: unknown, type: string): string {
  if (Array.isArray(value)) return `was ${value.length} ${value.length === 1 ? 'row' : 'rows'}`
  if (isWindow(value)) return `was ${cut(windowWords(value) || '—')}`
  return `was ${cut(formatCell(value, type))}`
}

export function referenceSource(label: string, asOf?: string | null): FieldSource {
  return { kind: 'reference', label, at: asOf ?? null }
}

export function extractedSource(kind: string, at: string | null): FieldSource {
  return { kind: 'extracted', label: kind, at }
}

function stepWords(s: FieldSource): string {
  if (s.step_number && s.step_title) return `${s.step_number} ${s.step_title}`
  return s.step_title ?? s.step_id ?? 'an earlier step'
}

function eventWords(kind: string | null | undefined): string {
  const label = kind ? (EVENT_LABELS[kind] ?? kind.replace(/_/g, ' ')) : 'event'
  return `the ${label.toLowerCase()}`
}

function join(parts: (string | null | undefined)[]): string {
  return parts.filter((p): p is string => !!p).join(' · ')
}

/** The status line's text for a source. Times through `whenText` (local, `now` injectable). */
export function sourceLine(source: FieldSource, now: Date = new Date(), zone?: string): string {
  const at = whenText(source.at ?? null, now, zone)
  switch (source.kind) {
    case 'person':
      return join([`from ${stepWords(source)}`, source.by_name, at, source.proposed ? "the AI's suggestion, kept" : null])
    case 'proposal':
      return join([SUGGESTED_BY_AI, formatConfidence(source.confidence)])
    case 'ai':
      return join([`by the AI at ${stepWords(source)}`, formatConfidence(source.confidence), at])
    case 'automation':
      return join([`by an automation at ${stepWords(source)}`, at])
    case 'event':
      return join([`from ${eventWords(source.event)}`, at])
    case 'intake':
      return join([`from ${VIA_WORDS[source.via ?? ''] ?? 'the enquiry'}`, at])
    case 'revision':
      return join([source.revision_number ? `from revision ${source.revision_number}` : 'from a revision', at])
    case 'extracted':
      return join([`extracted from ${EXTRACTED_WORDS[source.label ?? ''] ?? 'the enquiry'}`, at])
    case 'reference':
      return join([`from ${source.label ?? 'a reference'}`, source.at ? dayWords(source.at) : null])
    default:
      return PREFILLED
  }
}
